import { Box, Button, IconButton, Tooltip, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CodeOutlined from '@mui/icons-material/CodeOutlined';
import MenuOutlined from '@mui/icons-material/MenuOutlined';
import { IconCertificate, IconTemplate } from 'components/icons';

import LAYOUT_CONST from 'constant';
import useConfig from 'hooks/useConfig';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'store';
import { openDrawer } from 'store/slices/menu';
import keycloak from '../../../Keycloak';
import LogoSection from '../LogoSection';
import ProfileSection from './ProfileSection';
import SignSection from './SignSection';
import LocalizationSection from './LocalizationSection';
import { useFocusMainContent } from 'hooks/useFocusMainContent';

const Header = () => {
    const theme = useTheme();
    const intl = useIntl();

    const dispatch = useDispatch();
    const { drawerOpen } = useSelector((state) => state.menu);

    const matchDownMd = useMediaQuery(theme.breakpoints.down('md'));
    const { layout } = useConfig();

    const focusMainContent = useFocusMainContent();

    const headerButtonStyles = {
        backgroundColor: theme.palette.grey[100],
        color: theme.palette.grey[900],
        padding: '8px',
        minWidth: '0',
        borderRadius: '8px',
        '&:hover': {
            backgroundColor: theme.palette.grey[200]
        }
    };

    return (
        <>
            {/* logo & toggler button */}
            <Box
                sx={{
                    width: 228,
                    display: 'flex',
                    [theme.breakpoints.down('md')]: {
                        width: 'auto'
                    }
                }}
            >
                <Box component="span" sx={{ display: { xs: 'none', md: 'block' }, flexGrow: 1 }}>
                    <LogoSection />
                </Box>
                {(layout === LAYOUT_CONST.VERTICAL_LAYOUT || (layout === LAYOUT_CONST.HORIZONTAL_LAYOUT && matchDownMd)) && (
                    <IconButton
                        onClick={() => dispatch(openDrawer(!drawerOpen))}
                        aria-label={drawerOpen ? intl.formatMessage({ id: 'CERRAR_MENU' }) : intl.formatMessage({ id: 'ABRIR_MENU' })}
                        sx={{
                            borderRadius: 1,
                            ...theme.typography.commonAvatar,
                            ...theme.typography.mediumAvatar,

                            overflow: 'hidden',
                            transition: 'all .2s ease-in-out',
                            background: theme.palette.mode === 'dark' ? theme.palette.dark.main : theme.palette.primary.light,
                            color: theme.palette.mode === 'dark' ? theme.palette.primary.main : theme.palette.primary.dark,
                            '&:hover': {
                                background: theme.palette.mode === 'dark' ? theme.palette.primary.main : theme.palette.primary.dark,
                                color: theme.palette.mode === 'dark' ? theme.palette.primary.light : theme.palette.primary.light
                            }
                        }}
                    >
                        <MenuOutlined aria-hidden="true" sx={{ fontSize: "20px" }} />
                    </IconButton>
                )}
            </Box>

            <Box sx={{ flexGrow: 1 }} />

            {/* language selector */}
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                <LocalizationSection />
            </Box>

            {/* templates */}
            {!keycloak.realmAccess?.roles.includes('readonly') && (
                <Box sx={{ display: { xs: 'none', md: 'block' }, marginRight: '24px' }}>
                    <Tooltip title={intl.formatMessage({ id: 'PLANTILLAS' })}>
                        <Button
                            id="header-btn-templates"
                            aria-label={intl.formatMessage({ id: 'PLANTILLAS' })}
                            sx={headerButtonStyles}
                            component={Link}
                            to="/config/templates"
                            onClick={focusMainContent}
                        >
                            <IconTemplate stroke={1.5} aria-hidden="true" />
                        </Button>
                    </Tooltip>
                </Box>
            )}

            {/* developers */}
            {!keycloak.realmAccess?.roles.includes('readonly') && (
                <Box sx={{ display: { xs: 'none', md: 'block' }, marginRight: '24px' }}>
                    <Tooltip title={intl.formatMessage({ id: 'DESARROLLADORES' })}>
                        <Button
                            id="header-btn-developers"
                            aria-label={intl.formatMessage({ id: 'DESARROLLADORES' })}
                            sx={headerButtonStyles}
                            component={Link}
                            to="/config/developers"
                            onClick={focusMainContent}
                        >
                            <CodeOutlined aria-hidden="true" />
                        </Button>
                    </Tooltip>
                </Box>
            )}

            {/* certificates */}
            {!keycloak.realmAccess?.roles.includes('readonly') && (
                <Box sx={{ display: { xs: 'none', md: 'block' }, marginRight: '24px' }}>
                    <Tooltip title={intl.formatMessage({ id: 'CERTIFICADOS' })}>
                        <Button
                            id="header-btn-certificates"
                            aria-label={intl.formatMessage({ id: 'CERTIFICADOS' })}
                            sx={headerButtonStyles}
                            component={Link}
                            to="/config/certificates"
                            onClick={focusMainContent}
                        >
                            <IconCertificate stroke={1.5} aria-hidden="true" />
                        </Button>
                    </Tooltip>
                </Box>
            )}

            {/* saved graph signs */}
            {!keycloak.realmAccess?.roles.includes('readonly') && (
                <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                    <SignSection />
                </Box>
            )}

            {/* notification & profile */}
            <ProfileSection />
        </>
    );
};

export default Header;
